package music.jammers.client.shared.event.presentation.viewmodel

import androidx.compose.runtime.State
import androidx.compose.runtime.mutableStateOf
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDateTime
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import music.jammers.client.shared.analytics.event.EventAnalytics
import music.jammers.client.shared.component.BaseViewModel
import music.jammers.client.shared.component.FieldValidationState
import music.jammers.client.shared.component.customExceptionHandler
import music.jammers.client.shared.component.update
import music.jammers.client.shared.event.domain.usecase.ValidateEventUseCase
import music.jammers.client.shared.event.domain.validator.EventFieldValidator
import music.jammers.shared.event.domain.model.CardInfo
import music.jammers.shared.event.domain.model.EventData
import music.jammers.shared.event.domain.model.EventInstrument
import music.jammers.shared.instrument.domain.model.InstrumentType
import music.limasoul.client.shared.event.domain.usecase.CreateEventUseCase
import music.limasoul.client.shared.navigation.Router

class CreateEventViewModel(
    private val validateEventUseCase: ValidateEventUseCase,
    private val createEventUseCase: CreateEventUseCase,
    private val eventFieldValidator: EventFieldValidator,
    private val eventAnalytics: EventAnalytics,
    private val router: Router,
    initState: CreateEventState = CreateEventState(progress = true),
) : BaseViewModel() {

    internal val state: State<CreateEventState> get() = _state
    internal val validationState: State<FieldValidationState> get() = _validationState

    private val _state = mutableStateOf(initState)
    private val _validationState = mutableStateOf(FieldValidationState())

    private val digitRegex = "^\\d+$".toRegex()

    internal fun onReady() {
        _state.update {
            copy(
                progress = false,
            )
        }
    }

    internal fun onTitleChanged(value: String) {
        _state.update {
            copy(
                title = value,
            )
        }

        validateField(ValidateEventUseCase.TITLE, value)
    }

    internal fun onDescriptionChanged(value: String) {
        _state.update {
            copy(
                description = value,
            )
        }

        validateField(ValidateEventUseCase.DESCRIPTION, value)
    }

    internal fun onCardInfoChanged(value: CardInfo) {
        _state.update {
            copy(
                cardInfo = value,
            )
        }

        _validationState.update {
            resetError(ValidateEventUseCase.CARD_INFO)
        }
    }

    internal fun onDateChanged(value: LocalDateTime) {
        _state.update {
            copy(
                date = value,
            )
        }

        _validationState.update {
            resetError(ValidateEventUseCase.DATE)
        }
    }

    internal fun onGenreChanged(value: String) {
        _state.update {
            copy(
                genre = value,
            )
        }

        validateField(ValidateEventUseCase.GENRE, value)
    }

    internal fun onSpotChanged(value: String) {
        _state.update {
            copy(
                spot = value,
            )
        }

        validateField(ValidateEventUseCase.SPOT, value)
    }

    internal fun onSongsCountChanged(value: String) {
        if (value.isEmpty() || digitRegex.matches(value)) {
            _state.update {
                copy(
                    songsCount = value,
                )
            }

            validateField(ValidateEventUseCase.SONGS_COUNT, value)
        }
    }

    internal fun onAppliesChanged(value: String) {
        if (value.isEmpty() || digitRegex.matches(value)) {
            _state.update {
                copy(
                    appliesPerPerson = value,
                )
            }

            validateField(ValidateEventUseCase.APPLIES_PER_PERSON, value)
        }
    }

    internal fun onBackPressed() {
        router.back()
    }

    internal fun onInstrumentDeleted(index: Int) {
        val selected = state.value.instruments.toMutableList()
        selected.removeAt(index)

        _state.update {
            copy(instruments = selected)
        }

        validateInstruments(selected)
    }

    internal fun onInstrumentAddClicked() {
        _state.update {
            copy(
                bottomNavigationOpen = true,
            )
        }
    }

    internal fun onInstrumentClicked(index: Int) {
        val eventInstrument = _state.value.instruments[index]

        val newStatus = when (eventInstrument.status) {
            EventInstrument.Status.REQUIRED -> EventInstrument.Status.OPTIONAL
            EventInstrument.Status.OPTIONAL -> EventInstrument.Status.REQUIRED
            else -> error("Unsupported status ${eventInstrument.status}")
        }

        _state.update {
            val list = _state.value.instruments.toMutableList()

            list.removeAt(index)
            list.add(
                index,
                eventInstrument.copy(
                    status = newStatus,
                )
            )

            copy(
                instruments = list
            )
        }
    }

    internal fun onInstrumentAdded(instrumentType: InstrumentType) {
        val selected = state.value.instruments.toMutableList()
        selected.add(EventInstrument(instrumentType, EventInstrument.Status.REQUIRED))

        _state.update {
            copy(
                instruments = selected,
                bottomNavigationOpen = false,
            )
        }

        validateInstruments(selected)
    }

    internal fun onCreateClicked() {
        val stateValue = state.value

        val eventDataModel = EventData(
            title = stateValue.title,
            description = stateValue.description,
            date = stateValue.date ?: Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()),
            genre = stateValue.genre,
            spot = stateValue.spot,
            songsCount = stateValue.songsCount.toIntOrNull() ?: 0,
            appliesPerPerson = stateValue.appliesPerPerson.toIntOrNull() ?: 0,
            selectedInstrumentList = stateValue.instruments,
            cardInfo = stateValue.cardInfo ?: CardInfo.INVALID,
        )

        val errors = validateEventUseCase(eventDataModel)

        if (errors.isEmpty()) {
            createEvent(eventDataModel)
        } else {
            _validationState.update {
                copy(
                    validationErrorMap = errors.associateBy { item ->
                        item.fieldName
                    }
                )
            }
        }
    }

    private fun createEvent(eventDataModel: EventData) {
        _state.update {
            copy(
                progress = true
            )
        }

        launch(
            customExceptionHandler(router) { _, throwable ->
                _state.update {
                    copy(
                        progress = false,
                        error = throwable,
                    )
                }
                eventAnalytics.onError(throwable)
            }
        ) {
            val event = createEventUseCase.invoke(eventDataModel)
            eventAnalytics.onEventCreated(event.id)
            router.back()
        }
    }

    internal fun onBottomSheetClosed() {
        _state.update {
            copy(
                bottomNavigationOpen = false
            )
        }
    }

    internal fun onErrorDialogClosed() {
        _state.update {
            copy(
                progress = false,
                error = null,
            )
        }
    }

    private fun validateField(name: String, value: String) {
        _validationState.update {
            val error = eventFieldValidator.validate(name, value)
            if (error == null) {
                resetError(name)
            } else {
                addError(error)
            }
        }
    }

    private fun validateInstruments(selected: List<EventInstrument>) {
        _validationState.update {
            val error = eventFieldValidator.validate(selected)

            if (error == null) {
                resetError(ValidateEventUseCase.SELECTED_INSTRUMENTS)
            } else {
                addError(error)
            }
        }
    }
}